// Utils.tsx
//
// Shared, non-visual helpers for the app: the cached navigation role, readiness
// score maths, module lookup and progress, and signed video URLs. Everything
// here talks to Supabase through ../../api/queries rather than the client
// directly, so the data layer stays in one place.

import {
  createSignedStorageUrl,
  deleteModuleTaskProgress,
  fetchActiveModuleWithTasksByOrder,
  fetchActiveModules,
  fetchActiveModulesWithTasks,
  fetchEmployeeIdByAuthUserId,
  fetchEmployeeModuleFeedback,
  fetchEmployeeModuleFeedbackQuestions,
  fetchModuleById as fetchModuleByIdQuery,
  fetchModuleTaskProgress,
  fetchRecommendedModulesByEmployeeId,
  getCurrentUser,
  upsertEmployeeModuleFeedback,
  upsertModuleTaskDone,
} from "../../api/queries";
import type { CompanyRole } from "../../types/navigation";
import type {
  EmployeeModuleTaskProgress,
  EmployeeModuleFeedbackQuestion,
  EmployeeModuleFeedbackRow,
  Module,
  ModulePageKey,
  ModuleTaskCategory,
} from "../../types/modules";
import type { ScoreType } from "../../types/scores";

/* ------------------------------------------------------------------ *
 * Navigation role cache
 *
 * The visible role is cached in localStorage so the navigation bar can
 * render immediately on load instead of waiting for the profile query.
 * Every access is guarded: private browsing modes can throw on access.
 * ------------------------------------------------------------------ */

const NAV_ROLE_CACHE_KEY = "tk.nav.role";

export function loadCachedRole(): CompanyRole | null {
  try {
    return (localStorage.getItem(NAV_ROLE_CACHE_KEY) as CompanyRole) ?? null;
  } catch {
    return null;
  }
}

export function saveCachedRole(role: CompanyRole) {
  try {
    localStorage.setItem(NAV_ROLE_CACHE_KEY, role);
  } catch {}
}

/* ------------------------------------------------------------------ *
 * Readiness score calculations
 *
 * Each raw score uses its own scale and direction, so it is normalised
 * to a 0-100 "readiness" percentage before being shown or averaged.
 * PBG and Ext_PRS count downwards (a lower raw score is better).
 * ------------------------------------------------------------------ */

export function clampPercent(value: number) {
  return Math.max(0, Math.min(100, value));
}

export function readinessPercent(value: number | null, type: ScoreType) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;

  if (type === "PBG") {
    return clampPercent(((12.15 - value) / (12.15 - 4.19)) * 100);
  }

  if (type === "Ext_PRS") {
    return clampPercent(((50 - value) / 50) * 100);
  }

  return clampPercent((value / 3.08) * 100);
}

export function getOverallReadinessFromScores(scores: Record<ScoreType, number | null>) {
  const pbgNorm = readinessPercent(scores.PBG, "PBG");
  const prsNorm = readinessPercent(scores.Ext_PRS, "Ext_PRS");
  const eNorm = readinessPercent(scores.E_Score, "E_Score");

  if (pbgNorm === null || prsNorm === null || eNorm === null) return null;

  return clampPercent(pbgNorm * 0.35 + prsNorm * 0.35 + eNorm * 0.3);
}

/* ------------------------------------------------------------------ *
 * Modules: lookup and page routing
 *
 * Modules are matched to their page by id first, falling back to the
 * order index so a newly seeded module still resolves to a page.
 * ------------------------------------------------------------------ */

export async function listModules(): Promise<Module[]> {
  const { data, error } = await fetchActiveModules();

  if (error) throw error;
  return (data ?? []) as Module[];
}

export async function getModuleById(id: string): Promise<Module> {
  const { data, error } = await fetchModuleByIdQuery(id);

  if (error) throw error;
  return data as Module;
}

export const MODULE_PAGE_BY_ORDER: Record<number, ModulePageKey> = {
  1: "ModuleSupplies",
  2: "ModuleBackpack",
  3: "ModuleFirstAid",
  4: "ModuleDocuments",
  5: "ModuleCrisis",
  6: "ModuleEvacuation",
  7: "ModuleFitBody",
  8: "ModuleMentalStrength",
  9: "ModuleCommunity",
};

export const MODULE_PAGE_MAP: Record<string, ModulePageKey> = {
  "d9f0b39a-45ab-4ee7-9cfe-20983b1d2005": "ModuleSupplies",
  "a2cefe6c-5648-4654-889a-ad7e397e0238": "ModuleBackpack",
  "155f6fcf-d482-4c6a-b521-1a83e2af6cba": "ModuleFirstAid",
  "3e6b1050-b021-4ff7-939f-0dd316af0a8c": "ModuleDocuments",
  "d2c39987-fd70-4dde-9c44-ab65ebd1fb79": "ModuleCrisis",
  "0483f00e-62cb-4b04-b628-e98e27095b67": "ModuleEvacuation",
  "8bef3174-3bc8-4f4a-83a0-49826b6742af": "ModuleFitBody",
  "72d37728-2e73-4252-840c-efa94958727a": "ModuleMentalStrength",
  "eaf138a8-c5a0-43ed-ad76-2e592a508f71": "ModuleCommunity",
};

export function getModulePage(module: Pick<Module, "id" | "order_index">): ModulePageKey | null {
  if (module.id && MODULE_PAGE_MAP[module.id]) return MODULE_PAGE_MAP[module.id];

  const order = Number(module.order_index ?? 0);
  return MODULE_PAGE_BY_ORDER[order] ?? null;
}

/* ------------------------------------------------------------------ *
 * Action plan: module content and task progress
 * ------------------------------------------------------------------ */

export async function getMyEmployeeId(): Promise<string> {
  const { data: userData, error: userErr } = await getCurrentUser();

  if (userErr) throw userErr;
  if (!userData?.user) throw new Error("Not authenticated");

  const { data, error } = await fetchEmployeeIdByAuthUserId(userData.user.id);

  if (error) throw error;
  if (!data?.id) throw new Error("Employee record not found");

  return data.id;
}

function normalizeModule(module: Module): Module {
  const categories = (module.module_task_categories ?? [])
    .map((category) => ({
      ...category,
      module_tasks: (category.module_tasks ?? []).slice(),
    }))
    .sort((a, b) => Number(a.order_index ?? 0) - Number(b.order_index ?? 0));

  return {
    ...module,
    module_task_categories: categories,
  };
}

export async function fetchModules(): Promise<Module[]> {
  const { data, error } = await fetchActiveModulesWithTasks();

  if (error) throw error;

  return ((data ?? []) as unknown as Module[]).map(normalizeModule);
}

export async function fetchModuleByOrder(orderIndex: number): Promise<Module> {
  const { data, error } = await fetchActiveModuleWithTasksByOrder(orderIndex);

  if (error) throw error;
  return normalizeModule(data as unknown as Module);
}

export async function fetchMyModuleTaskProgress(
  employeeId: string
): Promise<Record<string, boolean>> {
  const { data, error } = await fetchModuleTaskProgress(employeeId);

  if (error) throw error;

  const map: Record<string, boolean> = {};

  for (const row of (data ?? []) as Pick<
    EmployeeModuleTaskProgress,
    "module_task_id" | "is_done"
  >[]) {
    map[row.module_task_id] = row.is_done;
  }

  return map;
}

export async function setModuleTaskDone(params: {
  employeeId: string;
  moduleTaskId: string;
  isDone: boolean;
}) {
  if (params.isDone) {
    const { error } = await upsertModuleTaskDone(params.employeeId, params.moduleTaskId);

    if (error) throw error;
    return;
  }

  const { error } = await deleteModuleTaskProgress(params.employeeId, params.moduleTaskId);

  if (error) throw error;
}

export async function fetchModuleFeedbackQuestions(): Promise<EmployeeModuleFeedbackQuestion[]> {
  const { data, error } = await fetchEmployeeModuleFeedbackQuestions();

  if (error) throw error;

  return data ?? [];
}

export async function fetchMyModuleFeedback(params: {
  employeeId: string;
  moduleId: string;
}): Promise<EmployeeModuleFeedbackRow | null> {
  const { data, error } = await fetchEmployeeModuleFeedback(params.employeeId, params.moduleId);

  if (error) throw error;

  return data ?? null;
}

export async function saveModuleFeedback(params: {
  employeeId: string;
  moduleId: string;
  moduleOrder: number;
  usefulnessRating: number;
  helpfulnessNote: string;
}): Promise<EmployeeModuleFeedbackRow> {
  const { data, error } = await upsertEmployeeModuleFeedback(params);

  if (error) throw error;
  if (!data) throw new Error("Module feedback was not saved.");

  return data;
}

export function getModuleTaskCount(module: Module) {
  return (module.module_task_categories ?? []).reduce(
    (sum, category: ModuleTaskCategory) => sum + (category.module_tasks ?? []).length,
    0
  );
}

export async function fetchMyRecommendedModuleIds(employeeId: string): Promise<Set<string>> {
  const { data, error } = await fetchRecommendedModulesByEmployeeId(employeeId);

  if (error) throw error;

  const ids = new Set<string>();

  for (const row of (data ?? []) as { module_id: string | null }[]) {
    if (row.module_id) ids.add(row.module_id);
  }

  return ids;
}

export function getModuleDoneCount(module: Module, progressMap: Record<string, boolean>) {
  return (module.module_task_categories ?? []).reduce((sum, category) => {
    const doneInCategory = (category.module_tasks ?? []).filter((task) => progressMap[task.id]).length;
    return sum + doneInCategory;
  }, 0);
}

/* ------------------------------------------------------------------ *
 * Signed video URLs
 *
 * Signed URLs expire, so they are cached twice: in memory for the
 * session, and in localStorage with an expiry so a reload does not
 * re-sign every video. Both caches fail soft.
 * ------------------------------------------------------------------ */

const memoryCache = new Map<string, string>();

export async function getSignedVideoUrl(bucket: string, path: string, expiresInSec = 3600): Promise<string | null> {
  // memory cache
  const mem = memoryCache.get(path);
  if (mem) return mem;

  // localStorage cache
  try {
    const raw = localStorage.getItem(`tk.video.${path}`);
    if (raw) {
      const parsed = JSON.parse(raw) as { url: string; expires: number };
      if (Date.now() < parsed.expires) {
        memoryCache.set(path, parsed.url);
        return parsed.url;
      }
      localStorage.removeItem(`tk.video.${path}`);
    }
  } catch {
    // ignore
  }

  // signed URL
  const { data, error } = await createSignedStorageUrl(bucket, path, expiresInSec);

  if (error || !data?.signedUrl) return null;

  memoryCache.set(path, data.signedUrl);

  try {
    localStorage.setItem(
      `tk.video.${path}`,
      JSON.stringify({
        url: data.signedUrl,
        expires: Date.now() + expiresInSec * 1000,
      })
    );
  } catch {
    // ignore
  }

  return data.signedUrl;
}
